/**
 * Human-style logical solver.
 *
 * Every function here finds a single deductive step a human could make,
 * expressed as cells to place and/or candidates to eliminate, plus the cells,
 * units and digits that form the pattern (so the UI can highlight them and the
 * hint engine can explain them).
 *
 * The same engine powers three things:
 *   - difficulty grading (the hardest technique required to crack a puzzle),
 *   - the in-game hint/teacher ("here's the next logical move"),
 *   - the generator's guarantee that every puzzle is solvable by known logic.
 *
 * Techniques are tried easiest-first so grading reflects the *minimal* hardest
 * technique a solver would actually need.
 */

import {
  ALL_DIGITS_MASK,
  BOX_UNITS,
  COL_UNITS,
  CELL_COUNT,
  Grid,
  PEERS,
  ROW_UNITS,
  UNITS,
  boxOf,
  colOf,
  digitsOf,
  firstDigit,
  hasDigit,
  maskOf,
  popcount,
  rowOf,
} from "./board.js";

export type TechniqueId =
  | "nakedSingle"
  | "hiddenSingle"
  | "pointing"
  | "claiming"
  | "nakedPair"
  | "hiddenPair"
  | "nakedTriple"
  | "hiddenTriple"
  | "nakedQuad"
  | "hiddenQuad"
  | "xWing"
  | "xyWing"
  | "xyzWing"
  | "swordfish"
  | "jellyfish"
  // Extra techniques, used for the Learn tutorials (and detectors), but not in
  // the main solving ladder, so difficulty grading & generation stay stable.
  | "skyscraper"
  | "twoStringKite"
  | "wWing"
  | "uniqueRectangle"
  | "fullHouse"
  | "uniqueRectangleType2"
  | "uniqueRectangleType4"
  | "bug"
  | "simpleColoring"
  | "remotePairs"
  | "emptyRectangle"
  | "xyChain"
  | "finnedXWing"
  | "finnedSwordfish"
  | "hiddenRectangle"
  | "xChain"
  | "wxyzWing"
  | "alsXZ";

export interface Placement {
  cell: number;
  digit: number;
}

export interface Elimination {
  cell: number;
  digit: number;
}

export interface Step {
  technique: TechniqueId;
  placements: Placement[];
  eliminations: Elimination[];
  /** Cells that form the pattern (highlighted strongly in the UI). */
  focusCells: number[];
  /** Unit indices (into UNITS) involved in the pattern. */
  units: number[];
  /** Digits the technique reasons about. */
  digits: number[];
}

export interface SolverState {
  values: Int8Array; // 0 = empty
  cand: Uint16Array; // 9-bit candidate masks; 0 for filled cells
}

/** Tier 1..5 of each technique → maps onto the five difficulty levels. */
export const TECHNIQUE_TIER: Record<TechniqueId, number> = {
  nakedSingle: 1,
  hiddenSingle: 1,
  pointing: 2,
  claiming: 2,
  nakedPair: 2,
  hiddenPair: 2,
  nakedTriple: 3,
  hiddenTriple: 3,
  nakedQuad: 3,
  hiddenQuad: 3,
  xWing: 3,
  xyWing: 4,
  xyzWing: 4,
  swordfish: 4,
  jellyfish: 5,
  skyscraper: 4,
  twoStringKite: 4,
  wWing: 4,
  uniqueRectangle: 4,
  fullHouse: 1,
  uniqueRectangleType2: 4,
  uniqueRectangleType4: 4,
  bug: 5,
  simpleColoring: 4,
  remotePairs: 4,
  emptyRectangle: 4,
  xyChain: 5,
  finnedXWing: 4,
  finnedSwordfish: 5,
  hiddenRectangle: 4,
  xChain: 5,
  wxyzWing: 4,
  alsXZ: 5,
};

export const TECHNIQUE_NAME: Record<TechniqueId, string> = {
  nakedSingle: "Naked Single",
  hiddenSingle: "Hidden Single",
  pointing: "Pointing Pair/Triple",
  claiming: "Box/Line Reduction",
  nakedPair: "Naked Pair",
  hiddenPair: "Hidden Pair",
  nakedTriple: "Naked Triple",
  hiddenTriple: "Hidden Triple",
  nakedQuad: "Naked Quad",
  hiddenQuad: "Hidden Quad",
  xWing: "X-Wing",
  xyWing: "XY-Wing",
  xyzWing: "XYZ-Wing",
  swordfish: "Swordfish",
  jellyfish: "Jellyfish",
  skyscraper: "Skyscraper",
  twoStringKite: "Two-String Kite",
  wWing: "W-Wing",
  uniqueRectangle: "Unique Rectangle",
  fullHouse: "Full House",
  uniqueRectangleType2: "Unique Rectangle (Type 2)",
  uniqueRectangleType4: "Unique Rectangle (Type 4)",
  bug: "BUG + 1",
  simpleColoring: "Simple coloring",
  remotePairs: "Remote Pairs",
  emptyRectangle: "Empty Rectangle",
  xyChain: "XY-Chain",
  finnedXWing: "Finned X-Wing",
  finnedSwordfish: "Finned Swordfish",
  hiddenRectangle: "Hidden Rectangle",
  xChain: "X-Chain",
  wxyzWing: "WXYZ-Wing",
  alsXZ: "ALS-XZ",
};

// ---------------------------------------------------------------------------
// State helpers
// ---------------------------------------------------------------------------

export function createSolverState(grid: Grid): SolverState {
  const values = grid.slice() as Int8Array;
  const cand = new Uint16Array(CELL_COUNT);
  for (let i = 0; i < CELL_COUNT; i++) {
    if (values[i] !== 0) continue;
    let used = 0;
    for (const p of PEERS[i]) {
      const v = values[p];
      if (v !== 0) used |= 1 << (v - 1);
    }
    cand[i] = ALL_DIGITS_MASK & ~used;
  }
  return { values, cand };
}

export function cloneState(s: SolverState): SolverState {
  return { values: s.values.slice(), cand: s.cand.slice() };
}

function assign(s: SolverState, cell: number, digit: number): void {
  s.values[cell] = digit;
  s.cand[cell] = 0;
  const bit = ~maskOf(digit);
  for (const p of PEERS[cell]) s.cand[p] &= bit;
}

export function applyStep(s: SolverState, step: Step): void {
  for (const p of step.placements) assign(s, p.cell, p.digit);
  for (const e of step.eliminations) s.cand[e.cell] &= ~maskOf(e.digit);
}

/** Generate index combinations: choose `k` of `n` (yields arrays of indices). */
function* combos(n: number, k: number): Generator<number[]> {
  if (k > n || k <= 0) return;
  const idx = Array.from({ length: k }, (_, i) => i);
  for (;;) {
    yield idx.slice();
    let i = k - 1;
    while (i >= 0 && idx[i] === n - k + i) i--;
    if (i < 0) return;
    idx[i]++;
    for (let j = i + 1; j < k; j++) idx[j] = idx[j - 1] + 1;
  }
}

const emptyOf = (s: SolverState, cells: readonly number[]): number[] =>
  cells.filter((c) => s.values[c] === 0);

// ---------------------------------------------------------------------------
// Tier 1, singles
// ---------------------------------------------------------------------------

function nakedSingle(s: SolverState): Step | null {
  for (let c = 0; c < CELL_COUNT; c++) {
    if (s.values[c] !== 0) continue;
    if (popcount(s.cand[c]) === 1) {
      const digit = firstDigit(s.cand[c]);
      return {
        technique: "nakedSingle",
        placements: [{ cell: c, digit }],
        eliminations: [],
        focusCells: [c],
        units: [],
        digits: [digit],
      };
    }
  }
  return null;
}

function hiddenSingle(s: SolverState): Step | null {
  for (let u = 0; u < UNITS.length; u++) {
    const unit = UNITS[u];
    for (let d = 1; d <= 9; d++) {
      let found = -1;
      let count = 0;
      for (const c of unit) {
        if (s.values[c] === 0 && hasDigit(s.cand[c], d)) {
          found = c;
          if (++count > 1) break;
        }
      }
      if (count === 1 && popcount(s.cand[found]) > 1) {
        return {
          technique: "hiddenSingle",
          placements: [{ cell: found, digit: d }],
          eliminations: [],
          focusCells: [found],
          units: [u],
          digits: [d],
        };
      }
    }
  }
  return null;
}

// ---------------------------------------------------------------------------
// Tier 2, locked candidates (pointing / claiming)
// ---------------------------------------------------------------------------

function pointing(s: SolverState): Step | null {
  for (let b = 0; b < 9; b++) {
    const box = BOX_UNITS[b];
    const boxUnit = 18 + b;
    for (let d = 1; d <= 9; d++) {
      const pos = box.filter((c) => s.values[c] === 0 && hasDigit(s.cand[c], d));
      if (pos.length < 2) continue;
      const sameRow = pos.every((c) => rowOf(c) === rowOf(pos[0]));
      const sameCol = pos.every((c) => colOf(c) === colOf(pos[0]));
      if (sameRow) {
        const r = rowOf(pos[0]);
        const elims: Elimination[] = [];
        for (const c of ROW_UNITS[r])
          if (boxOf(c) !== b && s.values[c] === 0 && hasDigit(s.cand[c], d))
            elims.push({ cell: c, digit: d });
        if (elims.length)
          return {
            technique: "pointing",
            placements: [],
            eliminations: elims,
            focusCells: pos,
            units: [boxUnit, r],
            digits: [d],
          };
      }
      if (sameCol) {
        const col = colOf(pos[0]);
        const elims: Elimination[] = [];
        for (const c of COL_UNITS[col])
          if (boxOf(c) !== b && s.values[c] === 0 && hasDigit(s.cand[c], d))
            elims.push({ cell: c, digit: d });
        if (elims.length)
          return {
            technique: "pointing",
            placements: [],
            eliminations: elims,
            focusCells: pos,
            units: [boxUnit, 9 + col],
            digits: [d],
          };
      }
    }
  }
  return null;
}

function claiming(s: SolverState): Step | null {
  const lines = [...ROW_UNITS, ...COL_UNITS];
  for (let li = 0; li < lines.length; li++) {
    const line = lines[li];
    const lineUnit = li; // ROW_UNITS occupy 0..8, COL_UNITS 9..17 in UNITS too
    for (let d = 1; d <= 9; d++) {
      const pos = line.filter((c) => s.values[c] === 0 && hasDigit(s.cand[c], d));
      if (pos.length < 2) continue;
      const b = boxOf(pos[0]);
      if (!pos.every((c) => boxOf(c) === b)) continue;
      const elims: Elimination[] = [];
      for (const c of BOX_UNITS[b])
        if (!line.includes(c) && s.values[c] === 0 && hasDigit(s.cand[c], d))
          elims.push({ cell: c, digit: d });
      if (elims.length)
        return {
          technique: "claiming",
          placements: [],
          eliminations: elims,
          focusCells: pos,
          units: [lineUnit, 18 + b],
          digits: [d],
        };
    }
  }
  return null;
}

// ---------------------------------------------------------------------------
// Tier 2/3, naked & hidden subsets
// ---------------------------------------------------------------------------

const NAKED_ID: Record<number, TechniqueId> = {
  2: "nakedPair",
  3: "nakedTriple",
  4: "nakedQuad",
};
const HIDDEN_ID: Record<number, TechniqueId> = {
  2: "hiddenPair",
  3: "hiddenTriple",
  4: "hiddenQuad",
};

function nakedSubset(s: SolverState, size: number): Step | null {
  for (let u = 0; u < UNITS.length; u++) {
    const cells = emptyOf(s, UNITS[u]).filter((c) => {
      const n = popcount(s.cand[c]);
      return n >= 2 && n <= size;
    });
    if (cells.length < size) continue;
    for (const combo of combos(cells.length, size)) {
      let union = 0;
      for (const ci of combo) union |= s.cand[cells[ci]];
      if (popcount(union) !== size) continue;
      const comboCells = combo.map((ci) => cells[ci]);
      const inCombo = new Set(comboCells);
      const elims: Elimination[] = [];
      for (const c of UNITS[u]) {
        if (s.values[c] !== 0 || inCombo.has(c)) continue;
        const shared = s.cand[c] & union;
        for (const d of digitsOf(shared)) elims.push({ cell: c, digit: d });
      }
      if (elims.length)
        return {
          technique: NAKED_ID[size],
          placements: [],
          eliminations: elims,
          focusCells: comboCells,
          units: [u],
          digits: digitsOf(union),
        };
    }
  }
  return null;
}

function hiddenSubset(s: SolverState, size: number): Step | null {
  for (let u = 0; u < UNITS.length; u++) {
    const unit = UNITS[u];
    const posOf = new Map<number, number[]>();
    for (let d = 1; d <= 9; d++) {
      const arr = unit.filter((c) => s.values[c] === 0 && hasDigit(s.cand[c], d));
      if (arr.length >= 2 && arr.length <= size) posOf.set(d, arr);
    }
    const digitsList = [...posOf.keys()];
    if (digitsList.length < size) continue;
    for (const combo of combos(digitsList.length, size)) {
      const ds = combo.map((ci) => digitsList[ci]);
      const cellSet = new Set<number>();
      for (const d of ds) for (const c of posOf.get(d)!) cellSet.add(c);
      if (cellSet.size !== size) continue;
      const dsMask = ds.reduce((m, d) => m | maskOf(d), 0);
      const elims: Elimination[] = [];
      for (const c of cellSet) {
        const extra = s.cand[c] & ~dsMask;
        for (const d of digitsOf(extra)) elims.push({ cell: c, digit: d });
      }
      if (elims.length)
        return {
          technique: HIDDEN_ID[size],
          placements: [],
          eliminations: elims,
          focusCells: [...cellSet],
          units: [u],
          digits: ds,
        };
    }
  }
  return null;
}

// ---------------------------------------------------------------------------
// Tier 3/4/5, basic fish (X-Wing / Swordfish / Jellyfish)
// ---------------------------------------------------------------------------

function fish(s: SolverState, size: number, id: TechniqueId): Step | null {
  // Try both orientations: rows as base lines (cover = columns), then columns.
  for (let orient = 0; orient < 2; orient++) {
    const baseUnits = orient === 0 ? ROW_UNITS : COL_UNITS;
    const baseOffset = orient === 0 ? 0 : 9; // unit index offset
    const crossOffset = orient === 0 ? 9 : 0;
    const lineIndex = (cell: number) => (orient === 0 ? colOf(cell) : rowOf(cell));
    const crossUnits = orient === 0 ? COL_UNITS : ROW_UNITS;

    for (let d = 1; d <= 9; d++) {
      // For each base line, the cross-indices where d is a candidate.
      const positions: { base: number; cross: number[] }[] = [];
      for (let bi = 0; bi < 9; bi++) {
        const cross: number[] = [];
        for (const c of baseUnits[bi])
          if (s.values[c] === 0 && hasDigit(s.cand[c], d)) cross.push(lineIndex(c));
        if (cross.length >= 2 && cross.length <= size) positions.push({ base: bi, cross });
      }
      if (positions.length < size) continue;
      for (const combo of combos(positions.length, size)) {
        const chosen = combo.map((ci) => positions[ci]);
        const coverSet = new Set<number>();
        for (const p of chosen) for (const x of p.cross) coverSet.add(x);
        if (coverSet.size !== size) continue;
        const baseSet = new Set(chosen.map((p) => p.base));
        const elims: Elimination[] = [];
        for (const cross of coverSet) {
          for (const c of crossUnits[cross]) {
            const base = orient === 0 ? rowOf(c) : colOf(c);
            if (baseSet.has(base)) continue;
            if (s.values[c] === 0 && hasDigit(s.cand[c], d)) elims.push({ cell: c, digit: d });
          }
        }
        if (elims.length) {
          const focusCells: number[] = [];
          for (const p of chosen)
            for (const c of baseUnits[p.base])
              if (s.values[c] === 0 && hasDigit(s.cand[c], d)) focusCells.push(c);
          const unitIdx = [
            ...chosen.map((p) => baseOffset + p.base),
            ...[...coverSet].map((x) => crossOffset + x),
          ];
          return {
            technique: id,
            placements: [],
            eliminations: elims,
            focusCells,
            units: unitIdx,
            digits: [d],
          };
        }
      }
    }
  }
  return null;
}

// ---------------------------------------------------------------------------
// Tier 4, XY-Wing & XYZ-Wing
// ---------------------------------------------------------------------------

function bivalueCells(s: SolverState): number[] {
  const out: number[] = [];
  for (let c = 0; c < CELL_COUNT; c++)
    if (s.values[c] === 0 && popcount(s.cand[c]) === 2) out.push(c);
  return out;
}

function seesBoth(a: number, b: number): number[] {
  const pa = new Set(PEERS[a]);
  return PEERS[b].filter((c) => pa.has(c));
}

function xyWing(s: SolverState): Step | null {
  const bivalue = bivalueCells(s);
  const bivalueSet = new Set(bivalue);
  for (const pivot of bivalue) {
    const pc = s.cand[pivot];
    const [x, y] = digitsOf(pc);
    const wings = PEERS[pivot].filter((c) => bivalueSet.has(c));
    for (let i = 0; i < wings.length; i++) {
      for (let j = i + 1; j < wings.length; j++) {
        const A = wings[i];
        const B = wings[j];
        const ca = s.cand[A];
        const cb = s.cand[B];
        const aHasX = hasDigit(ca, x);
        const aHasY = hasDigit(ca, y);
        const bHasX = hasDigit(cb, x);
        const bHasY = hasDigit(cb, y);
        // Each wing shares exactly one pivot digit, and they differ.
        if (aHasX === aHasY || bHasX === bHasY || aHasX === bHasX) continue;
        const zA = digitsOf(ca & ~pc);
        const zB = digitsOf(cb & ~pc);
        if (zA.length !== 1 || zB.length !== 1 || zA[0] !== zB[0]) continue;
        const z = zA[0];
        const targets = seesBoth(A, B).filter(
          (c) => c !== pivot && s.values[c] === 0 && hasDigit(s.cand[c], z),
        );
        if (targets.length) {
          return {
            technique: "xyWing",
            placements: [],
            eliminations: targets.map((c) => ({ cell: c, digit: z })),
            focusCells: [pivot, A, B],
            units: [],
            digits: [x, y, z],
          };
        }
      }
    }
  }
  return null;
}

function xyzWing(s: SolverState): Step | null {
  const bivalue = new Set(bivalueCells(s));
  for (let pivot = 0; pivot < CELL_COUNT; pivot++) {
    if (s.values[pivot] !== 0 || popcount(s.cand[pivot]) !== 3) continue;
    const pc = s.cand[pivot];
    const wings = PEERS[pivot].filter(
      (c) => bivalue.has(c) && (s.cand[c] & ~pc) === 0,
    );
    for (let i = 0; i < wings.length; i++) {
      for (let j = i + 1; j < wings.length; j++) {
        const A = wings[i];
        const B = wings[j];
        if ((s.cand[A] | s.cand[B]) !== pc) continue;
        const inter = s.cand[A] & s.cand[B];
        if (popcount(inter) !== 1) continue;
        const z = firstDigit(inter);
        // Target must see pivot AND both wings.
        const pivotPeers = new Set(PEERS[pivot]);
        const targets = seesBoth(A, B).filter(
          (c) =>
            c !== pivot &&
            pivotPeers.has(c) &&
            s.values[c] === 0 &&
            hasDigit(s.cand[c], z),
        );
        if (targets.length) {
          return {
            technique: "xyzWing",
            placements: [],
            eliminations: targets.map((c) => ({ cell: c, digit: z })),
            focusCells: [pivot, A, B],
            units: [],
            digits: digitsOf(pc),
          };
        }
      }
    }
  }
  return null;
}

// ---------------------------------------------------------------------------
// Extra single-digit & uniqueness techniques (Learn tutorials / detectors).
// These are NOT in the solving ladder, so grading/generation are unaffected.
// ---------------------------------------------------------------------------

/** Skyscraper: two conjugate pairs of a digit sharing one "base" line. */
function skyscraper(s: SolverState): Step | null {
  for (let orient = 0; orient < 2; orient++) {
    const lines = orient === 0 ? ROW_UNITS : COL_UNITS;
    const crossOf = orient === 0 ? colOf : rowOf;
    for (let d = 1; d <= 9; d++) {
      const conj: number[][] = [];
      for (let li = 0; li < 9; li++) {
        const cells = lines[li].filter((c) => s.values[c] === 0 && hasDigit(s.cand[c], d));
        if (cells.length === 2) conj.push(cells);
      }
      for (let i = 0; i < conj.length; i++) {
        for (let j = i + 1; j < conj.length; j++) {
          const A = conj[i];
          const B = conj[j];
          const shared = A.filter((c) => B.some((b) => crossOf(b) === crossOf(c)));
          if (shared.length !== 1) continue; // exactly one shared base line
          const base = crossOf(shared[0]);
          const roofA = A.find((c) => crossOf(c) !== base)!;
          const roofB = B.find((c) => crossOf(c) !== base)!;
          if (crossOf(roofA) === crossOf(roofB)) continue;
          const patt = new Set([...A, ...B]);
          const elims = seesBoth(roofA, roofB)
            .filter((c) => !patt.has(c) && s.values[c] === 0 && hasDigit(s.cand[c], d))
            .map((c) => ({ cell: c, digit: d }));
          if (elims.length) {
            return {
              technique: "skyscraper",
              placements: [],
              eliminations: elims,
              focusCells: [...patt],
              units: [],
              digits: [d],
            };
          }
        }
      }
    }
  }
  return null;
}

/** Two-String Kite: a row strong link and column strong link joined in a box. */
function twoStringKite(s: SolverState): Step | null {
  for (let d = 1; d <= 9; d++) {
    const rows: number[][] = [];
    for (let r = 0; r < 9; r++) {
      const cells = ROW_UNITS[r].filter((c) => s.values[c] === 0 && hasDigit(s.cand[c], d));
      if (cells.length === 2) rows.push(cells);
    }
    const cols: number[][] = [];
    for (let c = 0; c < 9; c++) {
      const cells = COL_UNITS[c].filter((x) => s.values[x] === 0 && hasDigit(s.cand[x], d));
      if (cells.length === 2) cols.push(cells);
    }
    for (const rc of rows) {
      for (const cc of cols) {
        const set = new Set([...rc, ...cc]);
        if (set.size !== 4) continue;
        for (const re of rc) {
          for (const ce of cc) {
            if (boxOf(re) !== boxOf(ce)) continue; // connected in a box
            const rLoose = rc.find((x) => x !== re)!;
            const cLoose = cc.find((x) => x !== ce)!;
            const target = rowOf(cLoose) * 9 + colOf(rLoose);
            if (set.has(target)) continue;
            if (s.values[target] === 0 && hasDigit(s.cand[target], d)) {
              return {
                technique: "twoStringKite",
                placements: [],
                eliminations: [{ cell: target, digit: d }],
                focusCells: [...set],
                units: [],
                digits: [d],
              };
            }
          }
        }
      }
    }
  }
  return null;
}

/** W-Wing: two identical bivalue cells linked by a strong link on one digit. */
function wWing(s: SolverState): Step | null {
  const biv = bivalueCells(s);
  for (let i = 0; i < biv.length; i++) {
    for (let j = i + 1; j < biv.length; j++) {
      const A = biv[i];
      const B = biv[j];
      if (s.cand[A] !== s.cand[B]) continue;
      if (PEERS[A].includes(B)) continue;
      const ds = digitsOf(s.cand[A]);
      const [x, y] = ds;
      for (const z of ds) {
        const w = z === x ? y : x;
        for (let u = 0; u < UNITS.length; u++) {
          const cells = UNITS[u].filter((c) => s.values[c] === 0 && hasDigit(s.cand[c], z));
          if (cells.length !== 2) continue;
          const [p, q] = cells;
          if (p === A || p === B || q === A || q === B) continue;
          const pa = PEERS[A].includes(p);
          const pb = PEERS[B].includes(p);
          const qa = PEERS[A].includes(q);
          const qb = PEERS[B].includes(q);
          if (!((pa && qb) || (pb && qa))) continue;
          const targets = seesBoth(A, B).filter(
            (c) => c !== A && c !== B && s.values[c] === 0 && hasDigit(s.cand[c], w),
          );
          if (targets.length) {
            return {
              technique: "wWing",
              placements: [],
              eliminations: targets.map((c) => ({ cell: c, digit: w })),
              focusCells: [A, B, p, q],
              units: [u],
              digits: [x, y],
            };
          }
        }
      }
    }
  }
  return null;
}

/** Unique Rectangle (Type 1): three corners share a bivalue pair across 2 boxes. */
function uniqueRectangle(s: SolverState): Step | null {
  for (let r1 = 0; r1 < 9; r1++) {
    for (let r2 = r1 + 1; r2 < 9; r2++) {
      for (let c1 = 0; c1 < 9; c1++) {
        for (let c2 = c1 + 1; c2 < 9; c2++) {
          const corners = [r1 * 9 + c1, r1 * 9 + c2, r2 * 9 + c1, r2 * 9 + c2];
          if (new Set(corners.map(boxOf)).size !== 2) continue;
          if (corners.some((c) => s.values[c] !== 0)) continue;
          for (let k = 0; k < 4; k++) {
            const extra = corners[k];
            const others = corners.filter((_, idx) => idx !== k);
            const pair = s.cand[others[0]];
            if (popcount(pair) !== 2) continue;
            if (!others.every((c) => s.cand[c] === pair)) continue;
            if ((s.cand[extra] & pair) !== pair) continue;
            if ((s.cand[extra] & ~pair) === 0) continue; // no extra candidate
            const elims = digitsOf(pair)
              .filter((d) => hasDigit(s.cand[extra], d))
              .map((d) => ({ cell: extra, digit: d }));
            if (elims.length) {
              return {
                technique: "uniqueRectangle",
                placements: [],
                eliminations: elims,
                focusCells: corners.slice(),
                units: [],
                digits: digitsOf(pair),
              };
            }
          }
        }
      }
    }
  }
  return null;
}

/** Full House: the last empty cell in a row, column or box. */
function fullHouse(s: SolverState): Step | null {
  for (let u = 0; u < UNITS.length; u++) {
    const empties = UNITS[u].filter((c) => s.values[c] === 0);
    if (empties.length === 1 && popcount(s.cand[empties[0]]) >= 1) {
      const c = empties[0];
      const d = firstDigit(s.cand[c]);
      return {
        technique: "fullHouse",
        placements: [{ cell: c, digit: d }],
        eliminations: [],
        focusCells: [c],
        units: [u],
        digits: [d],
      };
    }
  }
  return null;
}

/** Iterate rectangles that span exactly two boxes with four empty corners. */
function* rectangles(s: SolverState): Generator<number[]> {
  for (let r1 = 0; r1 < 9; r1++)
    for (let r2 = r1 + 1; r2 < 9; r2++)
      for (let c1 = 0; c1 < 9; c1++)
        for (let c2 = c1 + 1; c2 < 9; c2++) {
          const corners = [r1 * 9 + c1, r1 * 9 + c2, r2 * 9 + c1, r2 * 9 + c2];
          if (corners.some((c) => s.values[c] !== 0)) continue;
          if (new Set(corners.map(boxOf)).size !== 2) continue;
          yield corners;
        }
}

/** Unique Rectangle Type 2: two roof cells share one extra candidate. */
function uniqueRectangleType2(s: SolverState): Step | null {
  for (const corners of rectangles(s)) {
    const floors = corners.filter((c) => popcount(s.cand[c]) === 2);
    const roofs = corners.filter((c) => popcount(s.cand[c]) === 3);
    if (floors.length !== 2 || roofs.length !== 2) continue;
    const pair = s.cand[floors[0]];
    if (s.cand[floors[1]] !== pair) continue;
    if ((s.cand[roofs[0]] & pair) !== pair || (s.cand[roofs[1]] & pair) !== pair) continue;
    const z0 = s.cand[roofs[0]] & ~pair;
    if (z0 === 0 || z0 !== (s.cand[roofs[1]] & ~pair)) continue;
    const z = firstDigit(z0);
    const targets = seesBoth(roofs[0], roofs[1]).filter(
      (c) => !corners.includes(c) && s.values[c] === 0 && hasDigit(s.cand[c], z),
    );
    if (targets.length) {
      return {
        technique: "uniqueRectangleType2",
        placements: [],
        eliminations: targets.map((c) => ({ cell: c, digit: z })),
        focusCells: corners,
        units: [],
        digits: digitsOf(pair),
      };
    }
  }
  return null;
}

/** Unique Rectangle Type 4: a strong link on one UR digit kills the other. */
function uniqueRectangleType4(s: SolverState): Step | null {
  for (const corners of rectangles(s)) {
    const floors = corners.filter((c) => popcount(s.cand[c]) === 2);
    const roofs = corners.filter((c) => popcount(s.cand[c]) > 2);
    if (floors.length !== 2 || roofs.length !== 2) continue;
    const pair = s.cand[floors[0]];
    if (s.cand[floors[1]] !== pair) continue;
    if (!roofs.every((c) => (s.cand[c] & pair) === pair)) continue;
    const [a, b] = roofs;
    // The two roofs must share a row or a column to form the strong-link house.
    let house = -1;
    if (rowOf(a) === rowOf(b)) house = rowOf(a);
    else if (colOf(a) === colOf(b)) house = 9 + colOf(a);
    else continue;
    const [x, y] = digitsOf(pair);
    for (const locked of [x, y]) {
      const other = locked === x ? y : x;
      // `locked` confined to {a,b} within the house?
      let onlyHere = true;
      for (const c of UNITS[house]) {
        if (c === a || c === b) continue;
        if (s.values[c] === locked) {
          onlyHere = false;
          break;
        }
        if (s.values[c] === 0 && hasDigit(s.cand[c], locked)) {
          onlyHere = false;
          break;
        }
      }
      if (!onlyHere) continue;
      const elims = [a, b]
        .filter((c) => hasDigit(s.cand[c], other))
        .map((c) => ({ cell: c, digit: other }));
      if (elims.length) {
        return {
          technique: "uniqueRectangleType4",
          placements: [],
          eliminations: elims,
          focusCells: corners,
          units: [house],
          digits: [locked, other],
        };
      }
    }
  }
  return null;
}

/** BUG + 1: all unsolved cells are bivalue but one, place its odd digit. */
function bug(s: SolverState): Step | null {
  let tri = -1;
  for (let c = 0; c < CELL_COUNT; c++) {
    if (s.values[c] !== 0) continue;
    const n = popcount(s.cand[c]);
    if (n === 2) continue;
    if (n === 3) {
      if (tri !== -1) return null;
      tri = c;
    } else return null;
  }
  if (tri === -1) return null;
  const units = [rowOf(tri), 9 + colOf(tri), 18 + boxOf(tri)];
  for (const d of digitsOf(s.cand[tri])) {
    for (const u of units) {
      let count = 0;
      for (const c of UNITS[u]) if (s.values[c] === 0 && hasDigit(s.cand[c], d)) count++;
      if (count === 3) {
        return {
          technique: "bug",
          placements: [{ cell: tri, digit: d }],
          eliminations: [],
          focusCells: [tri],
          units: [u],
          digits: [d],
        };
      }
    }
  }
  return null;
}

/** Two-color a single digit's conjugate-pair graph; trap cells seeing both. */
function colorGraph(
  s: SolverState,
  d: number,
): { color: Map<number, number>; comp: Map<number, number> } {
  const bit = maskOf(d);
  const adj = new Map<number, number[]>();
  const link = (a: number, b: number) => {
    if (!adj.has(a)) adj.set(a, []);
    if (!adj.has(b)) adj.set(b, []);
    adj.get(a)!.push(b);
    adj.get(b)!.push(a);
  };
  for (let u = 0; u < UNITS.length; u++) {
    const pos = UNITS[u].filter((c) => s.values[c] === 0 && s.cand[c] & bit);
    if (pos.length === 2) link(pos[0], pos[1]);
  }
  const color = new Map<number, number>();
  const comp = new Map<number, number>();
  let id = 0;
  for (const start of adj.keys()) {
    if (color.has(start)) continue;
    const queue = [start];
    color.set(start, 0);
    comp.set(start, id);
    while (queue.length) {
      const cur = queue.shift()!;
      for (const nb of adj.get(cur) || [])
        if (!color.has(nb)) {
          color.set(nb, color.get(cur)! ^ 1);
          comp.set(nb, id);
          queue.push(nb);
        }
    }
    id++;
  }
  return { color, comp };
}

/** Simple coloring: a cell seeing both colors of a digit's chain loses it. */
function simpleColoring(s: SolverState): Step | null {
  for (let d = 1; d <= 9; d++) {
    const bit = maskOf(d);
    const { color, comp } = colorGraph(s, d);
    if (color.size < 4) continue;
    const elims: Elimination[] = [];
    for (let c = 0; c < CELL_COUNT; c++) {
      if (s.values[c] !== 0 || !(s.cand[c] & bit) || color.has(c)) continue;
      const c0 = new Set<number>();
      const c1 = new Set<number>();
      for (const p of PEERS[c])
        if (color.has(p)) (color.get(p) === 0 ? c0 : c1).add(comp.get(p)!);
      if ([...c0].some((k) => c1.has(k))) elims.push({ cell: c, digit: d });
    }
    if (elims.length) {
      return {
        technique: "simpleColoring",
        placements: [],
        eliminations: elims,
        focusCells: [...color.keys()],
        units: [],
        digits: [d],
      };
    }
  }
  return null;
}

/** Remote Pairs: a chain of identical bivalue cells; cells seeing both colors
 *  lose both digits. */
function remotePairs(s: SolverState): Step | null {
  const byPair = new Map<number, number[]>();
  for (let c = 0; c < CELL_COUNT; c++)
    if (s.values[c] === 0 && popcount(s.cand[c]) === 2) {
      const m = s.cand[c];
      if (!byPair.has(m)) byPair.set(m, []);
      byPair.get(m)!.push(c);
    }
  for (const [pair, cells] of byPair) {
    if (cells.length < 4) continue;
    const inSet = new Set(cells);
    const color = new Map<number, number>();
    const comp = new Map<number, number>();
    const size = new Map<number, number>();
    let id = 0;
    for (const start of cells) {
      if (color.has(start)) continue;
      const queue = [start];
      color.set(start, 0);
      comp.set(start, id);
      let n = 1;
      while (queue.length) {
        const cur = queue.shift()!;
        for (const p of PEERS[cur])
          if (inSet.has(p) && !color.has(p)) {
            color.set(p, color.get(cur)! ^ 1);
            comp.set(p, id);
            queue.push(p);
            n++;
          }
      }
      size.set(id, n);
      id++;
    }
    const [x, y] = digitsOf(pair);
    const elims: Elimination[] = [];
    for (let c = 0; c < CELL_COUNT; c++) {
      if (s.values[c] !== 0 || !(s.cand[c] & pair) || inSet.has(c)) continue;
      const c0 = new Set<number>();
      const c1 = new Set<number>();
      for (const p of PEERS[c])
        if (color.has(p)) (color.get(p) === 0 ? c0 : c1).add(comp.get(p)!);
      const trapped = [...c0].some((k) => c1.has(k) && (size.get(k) ?? 0) >= 4);
      if (trapped) for (const dd of [x, y]) if (hasDigit(s.cand[c], dd)) elims.push({ cell: c, digit: dd });
    }
    if (elims.length) {
      return {
        technique: "remotePairs",
        placements: [],
        eliminations: elims,
        focusCells: cells.slice(),
        units: [],
        digits: [x, y],
      };
    }
  }
  return null;
}

/** The other digit of a bivalue candidate mask. */
const otherDigit = (mask: number, d: number): number => firstDigit(mask & ~maskOf(d));

/** Empty Rectangle: a box where a digit's candidates share one row and one
 *  column, combined with a conjugate pair, eliminates the digit. */
function emptyRectangle(s: SolverState): Step | null {
  for (let b = 0; b < 9; b++) {
    const boxRowStart = Math.floor(b / 3) * 3;
    const boxColStart = (b % 3) * 3;
    for (let d = 1; d <= 9; d++) {
      const pos = BOX_UNITS[b].filter((c) => s.values[c] === 0 && hasDigit(s.cand[c], d));
      if (pos.length < 2) continue;
      const rows = [...new Set(pos.map(rowOf))];
      const cols = [...new Set(pos.map(colOf))];
      if (rows.length < 2 || cols.length < 2) continue; // a pointing pair otherwise
      for (const er of rows) {
        for (const ec of cols) {
          if (!pos.every((c) => rowOf(c) === er || colOf(c) === ec)) continue;
          // Column-based conjugate pair outside the box, with one end on row er.
          for (let cc = 0; cc < 9; cc++) {
            if (cc >= boxColStart && cc < boxColStart + 3) continue;
            const colCells = COL_UNITS[cc].filter((c) => s.values[c] === 0 && hasDigit(s.cand[c], d));
            if (colCells.length !== 2) continue;
            const onEr = colCells.find((c) => rowOf(c) === er);
            if (onEr === undefined) continue;
            const other = colCells.find((c) => c !== onEr)!;
            const target = rowOf(other) * 9 + ec;
            if (boxOf(target) === b) continue;
            if (s.values[target] === 0 && hasDigit(s.cand[target], d)) {
              return {
                technique: "emptyRectangle",
                placements: [],
                eliminations: [{ cell: target, digit: d }],
                focusCells: [...pos, onEr, other],
                units: [18 + b],
                digits: [d],
              };
            }
          }
          // Row-based conjugate pair outside the box, with one end on column ec.
          for (let rr = 0; rr < 9; rr++) {
            if (rr >= boxRowStart && rr < boxRowStart + 3) continue;
            const rowCells = ROW_UNITS[rr].filter((c) => s.values[c] === 0 && hasDigit(s.cand[c], d));
            if (rowCells.length !== 2) continue;
            const onEc = rowCells.find((c) => colOf(c) === ec);
            if (onEc === undefined) continue;
            const other = rowCells.find((c) => c !== onEc)!;
            const target = er * 9 + colOf(other);
            if (boxOf(target) === b) continue;
            if (s.values[target] === 0 && hasDigit(s.cand[target], d)) {
              return {
                technique: "emptyRectangle",
                placements: [],
                eliminations: [{ cell: target, digit: d }],
                focusCells: [...pos, onEc, other],
                units: [18 + b],
                digits: [d],
              };
            }
          }
        }
      }
    }
  }
  return null;
}

/** Finned fish: a fish (size 2 = X-Wing, 3 = Swordfish) where one base line has
 *  extra candidates (the "fin") all confined to a single box. The fish
 *  eliminations survive only where they also share that box with every fin. */
function finnedFish(s: SolverState, size: number, id: TechniqueId): Step | null {
  for (let orient = 0; orient < 2; orient++) {
    const baseUnits = orient === 0 ? ROW_UNITS : COL_UNITS;
    const crossUnits = orient === 0 ? COL_UNITS : ROW_UNITS;
    const baseOffset = orient === 0 ? 0 : 9;
    const crossOffset = orient === 0 ? 9 : 0;
    const crossIndex = (c: number) => (orient === 0 ? colOf(c) : rowOf(c));
    const baseIndex = (c: number) => (orient === 0 ? rowOf(c) : colOf(c));
    for (let d = 1; d <= 9; d++) {
      const cells: number[][] = [];
      const candBis: number[] = [];
      for (let bi = 0; bi < 9; bi++) {
        cells[bi] = baseUnits[bi].filter((c) => s.values[c] === 0 && hasDigit(s.cand[c], d));
        if (cells[bi].length >= 2) candBis.push(bi);
      }
      if (candBis.length < size) continue;
      for (const combo of combos(candBis.length, size)) {
        const bis = combo.map((i) => candBis[i]);
        const baseSet = new Set(bis);
        for (let fi = 0; fi < size; fi++) {
          const finnedBi = bis[fi];
          // Cover lines = the cross-indices spanned by the clean (non-finned) lines.
          const cover = new Set<number>();
          for (let k = 0; k < size; k++)
            if (k !== fi) for (const c of cells[bis[k]]) cover.add(crossIndex(c));
          if (cover.size !== size) continue;
          const finCells = cells[finnedBi].filter((c) => !cover.has(crossIndex(c)));
          if (finCells.length === 0) continue; // a plain fish, handled by fish()
          if (!cells[finnedBi].some((c) => cover.has(crossIndex(c)))) continue;
          const finBox = boxOf(finCells[0]);
          if (!finCells.every((c) => boxOf(c) === finBox)) continue;
          const elims: Elimination[] = [];
          for (const x of cover) {
            for (const c of crossUnits[x]) {
              if (baseSet.has(baseIndex(c))) continue;
              if (s.values[c] !== 0 || !hasDigit(s.cand[c], d)) continue;
              if (finCells.every((f) => PEERS[c].includes(f))) elims.push({ cell: c, digit: d });
            }
          }
          if (elims.length) {
            const focusCells: number[] = [];
            for (const bi of bis) for (const c of cells[bi]) focusCells.push(c);
            const units = [
              ...bis.map((bi) => baseOffset + bi),
              ...[...cover].map((x) => crossOffset + x),
              18 + finBox,
            ];
            return { technique: id, placements: [], eliminations: elims, focusCells, units, digits: [d] };
          }
        }
      }
    }
  }
  return null;
}

/** Hidden Rectangle: a uniqueness pattern. A bivalue floor {x,y} and the
 *  diagonally opposite corner, where one UR digit is conjugate-locked along
 *  both the corner's row and column, so the other digit drops from that corner. */
function hiddenRectangle(s: SolverState): Step | null {
  for (const corners of rectangles(s)) {
    const [A, B, C, D] = corners; // r1c1, r1c2, r2c1, r2c2
    const arrange = [
      { floor: A, diag: D, rowMate: B, colMate: C },
      { floor: B, diag: C, rowMate: A, colMate: D },
      { floor: C, diag: B, rowMate: D, colMate: A },
      { floor: D, diag: A, rowMate: C, colMate: B },
    ];
    for (const { floor, diag, rowMate, colMate } of arrange) {
      if (popcount(s.cand[floor]) !== 2) continue;
      const pair = s.cand[floor];
      if (corners.some((c) => (s.cand[c] & pair) !== pair)) continue;
      const [x, y] = digitsOf(pair);
      for (const lock of [x, y]) {
        const elimDigit = lock === x ? y : x;
        if (!hasDigit(s.cand[diag], elimDigit)) continue;
        const rowD = rowOf(diag);
        const colD = colOf(diag);
        const rowPos = ROW_UNITS[rowD].filter((c) => s.values[c] === 0 && hasDigit(s.cand[c], lock));
        const colPos = COL_UNITS[colD].filter((c) => s.values[c] === 0 && hasDigit(s.cand[c], lock));
        const rowOk = rowPos.length === 2 && rowPos.includes(colMate) && rowPos.includes(diag);
        const colOk = colPos.length === 2 && colPos.includes(rowMate) && colPos.includes(diag);
        if (rowOk && colOk) {
          return {
            technique: "hiddenRectangle",
            placements: [],
            eliminations: [{ cell: diag, digit: elimDigit }],
            focusCells: corners.slice(),
            units: [rowD, 9 + colD],
            digits: [x, y],
          };
        }
      }
    }
  }
  return null;
}

/** XY-Chain: a chain of bivalue cells, each sharing a digit with the next, whose
 *  ends both carry a digit z. Any cell seeing both ends cannot be z. */
function xyChain(s: SolverState): Step | null {
  const biv = bivalueCells(s);
  const bivSet = new Set(biv);
  const MAX = 8;
  let budget = 8000;
  for (const start of biv) {
    for (const z of digitsOf(s.cand[start])) {
      const stack: { cell: number; forced: number; path: number[] }[] = [
        { cell: start, forced: otherDigit(s.cand[start], z), path: [start] },
      ];
      while (stack.length) {
        if (--budget <= 0) return null;
        const cur = stack.pop()!;
        if (cur.forced === z && cur.path.length >= 4) {
          const end = cur.cell;
          const targets = seesBoth(start, end).filter(
            (c) =>
              c !== start &&
              c !== end &&
              !cur.path.includes(c) &&
              s.values[c] === 0 &&
              hasDigit(s.cand[c], z),
          );
          if (targets.length) {
            return {
              technique: "xyChain",
              placements: [],
              eliminations: targets.map((c) => ({ cell: c, digit: z })),
              focusCells: cur.path.slice(),
              units: [],
              digits: [z],
            };
          }
        }
        if (cur.path.length >= MAX) continue;
        for (const nb of PEERS[cur.cell]) {
          if (!bivSet.has(nb) || cur.path.includes(nb)) continue;
          if (!hasDigit(s.cand[nb], cur.forced)) continue;
          stack.push({
            cell: nb,
            forced: otherDigit(s.cand[nb], cur.forced),
            path: [...cur.path, nb],
          });
        }
      }
    }
  }
  return null;
}

/** X-Chain: a single-digit alternating chain of strong (conjugate) and weak
 *  (shared-house) links. With ≥3 strong links its two ends form a strong link,
 *  so any cell seeing both ends loses the digit. */
function xChain(s: SolverState): Step | null {
  for (let d = 1; d <= 9; d++) {
    const bit = maskOf(d);
    const isC = (c: number) => s.values[c] === 0 && (s.cand[c] & bit) !== 0;
    const strong = new Map<number, number[]>();
    const link = (a: number, b: number) => {
      let la = strong.get(a);
      if (!la) strong.set(a, (la = []));
      la.push(b);
      let lb = strong.get(b);
      if (!lb) strong.set(b, (lb = []));
      lb.push(a);
    };
    for (let u = 0; u < UNITS.length; u++) {
      const pos = UNITS[u].filter(isC);
      if (pos.length === 2) link(pos[0], pos[1]);
    }
    const starts = [...strong.keys()];
    if (starts.length < 4) continue;
    let budget = 6000;
    for (const start of starts) {
      const stack: { cell: number; mustStrong: boolean; links: number; path: number[] }[] = [
        { cell: start, mustStrong: true, links: 0, path: [start] },
      ];
      while (stack.length) {
        if (--budget <= 0) return null;
        const cur = stack.pop()!;
        // An endpoint is reached via a strong link (so the next must be weak) after
        // ≥5 links, i.e. three strong links, making the chain genuinely long.
        if (!cur.mustStrong && cur.links >= 5) {
          const targets = seesBoth(start, cur.cell).filter(
            (c) => c !== start && c !== cur.cell && !cur.path.includes(c) && isC(c),
          );
          if (targets.length) {
            return {
              technique: "xChain",
              placements: [],
              eliminations: targets.map((c) => ({ cell: c, digit: d })),
              focusCells: cur.path.slice(),
              units: [],
              digits: [d],
            };
          }
        }
        if (cur.path.length >= 9) continue;
        if (cur.mustStrong) {
          for (const nb of strong.get(cur.cell) ?? []) {
            if (cur.path.includes(nb)) continue;
            stack.push({ cell: nb, mustStrong: false, links: cur.links + 1, path: [...cur.path, nb] });
          }
        } else {
          for (const nb of PEERS[cur.cell]) {
            if (!isC(nb) || cur.path.includes(nb)) continue;
            stack.push({ cell: nb, mustStrong: true, links: cur.links + 1, path: [...cur.path, nb] });
          }
        }
      }
    }
  }
  return null;
}

/** An Almost Locked Set: cells in one house whose candidate union has exactly
 *  one more digit than there are cells (so one digit is "free"). */
interface Als {
  cells: number[];
  mask: number;
}

function enumerateAls(s: SolverState, maxSize: number): Als[] {
  const out: Als[] = [];
  const seen = new Set<string>();
  for (let u = 0; u < UNITS.length; u++) {
    const empties = UNITS[u].filter((c) => s.values[c] === 0);
    for (let size = 1; size <= maxSize && size <= empties.length; size++) {
      for (const combo of combos(empties.length, size)) {
        const cells = combo.map((i) => empties[i]);
        let mask = 0;
        for (const c of cells) mask |= s.cand[c];
        if (popcount(mask) === size + 1) {
          const key = cells.slice().sort((a, b) => a - b).join(",");
          if (!seen.has(key)) {
            seen.add(key);
            out.push({ cells: cells.slice(), mask });
          }
        }
      }
    }
  }
  return out;
}

/** ALS-XZ: two disjoint Almost Locked Sets sharing a "restricted common"
 *  candidate X (X can be placed in only one of them). For any other shared
 *  digit Z, one of the two sets is forced to contain Z, so any cell seeing
 *  every Z in both sets cannot be Z. `accept` shapes the pair for a lesson. */
function findAlsXZ(
  s: SolverState,
  id: TechniqueId,
  accept: (a: Als, b: Als) => boolean,
): Step | null {
  const list = enumerateAls(s, 3);
  let budget = 60000;
  for (let i = 0; i < list.length; i++) {
    for (let j = i + 1; j < list.length; j++) {
      if (--budget <= 0) return null;
      const A = list[i];
      const B = list[j];
      if (A.cells.some((c) => B.cells.includes(c))) continue;
      const common = A.mask & B.mask;
      if (popcount(common) < 2) continue;
      if (!accept(A, B)) continue;
      const commonDigits = digitsOf(common);
      for (const X of commonDigits) {
        const ax = A.cells.filter((c) => hasDigit(s.cand[c], X));
        const bx = B.cells.filter((c) => hasDigit(s.cand[c], X));
        // X is restricted-common iff every X in A sees every X in B.
        let restricted = true;
        for (const a of ax) {
          for (const b of bx) if (!PEERS[a].includes(b)) { restricted = false; break; }
          if (!restricted) break;
        }
        if (!restricted) continue;
        for (const Z of commonDigits) {
          if (Z === X) continue;
          const zCells = [
            ...A.cells.filter((c) => hasDigit(s.cand[c], Z)),
            ...B.cells.filter((c) => hasDigit(s.cand[c], Z)),
          ];
          const elims: Elimination[] = [];
          for (let c = 0; c < CELL_COUNT; c++) {
            if (s.values[c] !== 0 || !hasDigit(s.cand[c], Z)) continue;
            if (A.cells.includes(c) || B.cells.includes(c)) continue;
            if (zCells.every((z) => PEERS[c].includes(z))) elims.push({ cell: c, digit: Z });
          }
          if (elims.length) {
            return {
              technique: id,
              placements: [],
              eliminations: elims,
              focusCells: [...A.cells, ...B.cells],
              units: [],
              digits: [X, Z],
            };
          }
        }
      }
    }
  }
  return null;
}

/** WXYZ-Wing: the ALS-XZ special case of a three-cell ALS plus a bivalue cell
 *  that together hold exactly four digits. */
function wxyzWing(s: SolverState): Step | null {
  return findAlsXZ(s, "wxyzWing", (a, b) => {
    const lo = Math.min(a.cells.length, b.cells.length);
    const hi = Math.max(a.cells.length, b.cells.length);
    return lo === 1 && hi === 3 && popcount(a.mask | b.mask) === 4;
  });
}

/** ALS-XZ proper: a genuine two-set pattern (multi-cell, four+ cells total)
 *  beyond the smaller named wings. */
function alsXZ(s: SolverState): Step | null {
  return findAlsXZ(s, "alsXZ", (a, b) => {
    const total = a.cells.length + b.cells.length;
    const lo = Math.min(a.cells.length, b.cells.length);
    if (total < 4 || Math.max(a.cells.length, b.cells.length) < 2) return false;
    // Exclude the WXYZ-Wing shape so the two lessons stay distinct.
    return !(lo === 1 && popcount(a.mask | b.mask) === 4);
  });
}

// ---------------------------------------------------------------------------
// The ordered ladder + drivers
// ---------------------------------------------------------------------------

export interface TechniqueEntry {
  id: TechniqueId;
  tier: number;
  run: (s: SolverState) => Step | null;
}

/** Techniques in easiest-first order (drives both grading and hints). */
export const TECHNIQUES: readonly TechniqueEntry[] = [
  { id: "nakedSingle", tier: 1, run: nakedSingle },
  { id: "hiddenSingle", tier: 1, run: hiddenSingle },
  { id: "pointing", tier: 2, run: pointing },
  { id: "claiming", tier: 2, run: claiming },
  { id: "nakedPair", tier: 2, run: (s) => nakedSubset(s, 2) },
  { id: "hiddenPair", tier: 2, run: (s) => hiddenSubset(s, 2) },
  { id: "nakedTriple", tier: 3, run: (s) => nakedSubset(s, 3) },
  { id: "hiddenTriple", tier: 3, run: (s) => hiddenSubset(s, 3) },
  { id: "nakedQuad", tier: 3, run: (s) => nakedSubset(s, 4) },
  { id: "hiddenQuad", tier: 3, run: (s) => hiddenSubset(s, 4) },
  { id: "xWing", tier: 3, run: (s) => fish(s, 2, "xWing") },
  { id: "xyWing", tier: 4, run: xyWing },
  { id: "xyzWing", tier: 4, run: xyzWing },
  { id: "swordfish", tier: 4, run: (s) => fish(s, 3, "swordfish") },
  { id: "jellyfish", tier: 5, run: (s) => fish(s, 4, "jellyfish") },
];

/**
 * Extra techniques exposed for the Learn tutorials and for direct detection,
 * but deliberately kept OUT of the solving ladder above so that difficulty
 * grading and puzzle generation are unaffected by them.
 */
export const EXTRA_TECHNIQUES: readonly TechniqueEntry[] = [
  { id: "fullHouse", tier: 1, run: fullHouse },
  { id: "skyscraper", tier: 4, run: skyscraper },
  { id: "twoStringKite", tier: 4, run: twoStringKite },
  { id: "wWing", tier: 4, run: wWing },
  { id: "uniqueRectangle", tier: 4, run: uniqueRectangle },
  { id: "uniqueRectangleType2", tier: 4, run: uniqueRectangleType2 },
  { id: "uniqueRectangleType4", tier: 4, run: uniqueRectangleType4 },
  { id: "simpleColoring", tier: 4, run: simpleColoring },
  { id: "remotePairs", tier: 4, run: remotePairs },
  { id: "emptyRectangle", tier: 4, run: emptyRectangle },
  { id: "finnedXWing", tier: 4, run: (s) => finnedFish(s, 2, "finnedXWing") },
  { id: "hiddenRectangle", tier: 4, run: hiddenRectangle },
  { id: "bug", tier: 5, run: bug },
  { id: "finnedSwordfish", tier: 5, run: (s) => finnedFish(s, 3, "finnedSwordfish") },
  { id: "xyChain", tier: 5, run: xyChain },
  { id: "xChain", tier: 5, run: xChain },
  { id: "wxyzWing", tier: 4, run: wxyzWing },
  { id: "alsXZ", tier: 5, run: alsXZ },
];

/** Run a single technique (ladder or extra) by id on a grid. */
export function runTechniqueById(grid: Grid, id: TechniqueId): Step | null {
  const entry =
    TECHNIQUES.find((t) => t.id === id) ?? EXTRA_TECHNIQUES.find((t) => t.id === id);
  if (!entry) return null;
  return entry.run(createSolverState(grid));
}

/** Find the next logical step (for hints). `maxTier` caps technique difficulty. */
export function nextStep(s: SolverState, maxTier = 5): Step | null {
  for (const t of TECHNIQUES) {
    if (t.tier > maxTier) continue;
    const step = t.run(s);
    if (step) return step;
  }
  return null;
}

export interface LogicalSolveResult {
  solved: boolean;
  maxTier: number;
  steps: Step[];
  usedTechniques: Map<TechniqueId, number>;
  state: SolverState;
}

/**
 * Solve a grid using only logic, easiest technique first. Reports whether it
 * fully solved, the hardest tier required, and how often each technique fired.
 */
export function solveLogically(
  grid: Grid,
  opts: { maxTier?: number } = {},
): LogicalSolveResult {
  const maxTier = opts.maxTier ?? 5;
  const state = createSolverState(grid);
  const steps: Step[] = [];
  const used = new Map<TechniqueId, number>();
  let highestTier = 0;

  for (;;) {
    // Solved?
    let anyEmpty = false;
    for (let i = 0; i < CELL_COUNT; i++)
      if (state.values[i] === 0) {
        anyEmpty = true;
        break;
      }
    if (!anyEmpty) break;

    const step = nextStep(state, maxTier);
    if (!step) break; // stuck, needs a technique we don't implement

    applyStep(state, step);
    steps.push(step);
    used.set(step.technique, (used.get(step.technique) ?? 0) + 1);
    const tier = TECHNIQUE_TIER[step.technique];
    if (tier > highestTier) highestTier = tier;
  }

  let solved = true;
  for (let i = 0; i < CELL_COUNT; i++)
    if (state.values[i] === 0) {
      solved = false;
      break;
    }

  return { solved, maxTier: highestTier, steps, usedTechniques: used, state };
}
